import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuid } from 'uuid'
import { UploadedCreativeAsset } from '@storefronts/common-types'
import { R } from '@/modules/api/actions'

export const MAX_ASSETS = 10

export type ManualCreativeFormVariant = 'paid' | 'email'

export interface UploadedFile {
    id: string
    file: File
    type: 'IMAGE' | 'VIDEO'
    previewUrl: string
}

export interface ManualCreativePayload {
    assets: UploadedCreativeAsset[]
    adCopy?: string
    productUrl: string
    creativeName?: string
}

export interface VideoUploadProgress {
    label: string
    progress: number
}

export interface ManualCreativeUploadFormState {
    files: UploadedFile[]
    adCopy: string
    setAdCopy: (value: string) => void
    productUrl: string
    setProductUrl: (value: string) => void
    creativeName: string
    setCreativeName: (value: string) => void
    isUploading: boolean
    videoUpload: VideoUploadProgress | null
    canSubmit: boolean
    addFiles: (acceptedFiles: File[]) => void
    removeFile: (id: string) => void
    reset: () => void
    /** Validate, upload assets, and return the payload. Throws on failure. */
    uploadAndBuild: () => Promise<ManualCreativePayload>
}

export function useManualCreativeUploadForm(
    variant: ManualCreativeFormVariant = 'paid',
): ManualCreativeUploadFormState {
    const [files, setFiles] = useState<UploadedFile[]>([])
    const [adCopy, setAdCopy] = useState('')
    const [productUrl, setProductUrl] = useState('')
    const [creativeName, setCreativeName] = useState('')
    const [isUploading, setIsUploading] = useState(false)
    const [videoUpload, setVideoUpload] = useState<VideoUploadProgress | null>(
        null,
    )

    const filesRef = useRef<UploadedFile[]>([])
    const commitFiles = (next: UploadedFile[]) => {
        filesRef.current = next
        setFiles(next)
    }

    useEffect(() => {
        return () => {
            filesRef.current.forEach((f) => URL.revokeObjectURL(f.previewUrl))
        }
    }, [])

    const canSubmit =
        files.length > 0 || Boolean(adCopy.trim()) || Boolean(productUrl.trim())

    const addFiles = (acceptedFiles: File[]) => {
        const remaining = MAX_ASSETS - filesRef.current.length
        if (remaining <= 0) return

        const newFiles: UploadedFile[] = acceptedFiles
            .slice(0, remaining)
            .map((file) => ({
                id: uuid(),
                file,
                type: file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE',
                previewUrl: URL.createObjectURL(file),
            }))

        commitFiles([...filesRef.current, ...newFiles])

        if (acceptedFiles.length > remaining) {
            toast.error(
                `Maximum ${MAX_ASSETS} assets allowed. Only ${remaining} file${remaining === 1 ? '' : 's'} added.`,
            )
        }
    }

    const removeFile = (id: string) => {
        const target = filesRef.current.find((f) => f.id === id)
        if (target) URL.revokeObjectURL(target.previewUrl)
        commitFiles(filesRef.current.filter((f) => f.id !== id))
    }

    const reset = () => {
        filesRef.current.forEach((f) => URL.revokeObjectURL(f.previewUrl))
        commitFiles([])
        setAdCopy('')
        setProductUrl('')
        setCreativeName('')
    }

    const uploadFiles = async (): Promise<UploadedCreativeAsset[]> => {
        const uploadedAssets: UploadedCreativeAsset[] = []
        const videoTotal = files.filter((f) => f.type === 'VIDEO').length
        let videoIndex = 0

        for (const f of files) {
            if (f.type === 'VIDEO') {
                videoIndex += 1
                setVideoUpload({
                    label:
                        videoTotal > 1
                            ? `Uploading video ${videoIndex} of ${videoTotal}`
                            : 'Uploading video',
                    progress: 0,
                })

                const { uploadUrl, videoUrl } =
                    await R.createS3UploadUrlForVideo(f.file.type)
                await uploadVideoFile(f.file, uploadUrl, (progress) =>
                    setVideoUpload((prev) => prev && { ...prev, progress }),
                )
                uploadedAssets.push({
                    mediaType: 'VIDEO',
                    creativeUrl: videoUrl,
                })
            } else {
                setVideoUpload(null)
                const uploadResult = await R.uploadImage(f.file, {
                    name: creativeName || f.file.name,
                })
                uploadedAssets.push({
                    mediaType: 'IMAGE',
                    creativeUrl: uploadResult.image.imageUrl,
                })
            }
        }
        return uploadedAssets
    }

    const uploadAndBuild = async (): Promise<ManualCreativePayload> => {
        const trimmedAdCopy = adCopy.trim()
        const trimmedProductUrl = productUrl.trim()
        if (!canSubmit) {
            throw new Error(
                variant === 'email'
                    ? 'Please provide at least one of: creative file, message copy, or product URL'
                    : 'Please provide at least one of: creative file, ad copy, or product URL',
            )
        }
        if (trimmedProductUrl && !isValidUrl(trimmedProductUrl))
            throw new Error('Please enter a valid URL')

        setIsUploading(true)
        try {
            const assets = await uploadFiles()
            return {
                assets,
                adCopy: trimmedAdCopy || undefined,
                productUrl: trimmedProductUrl,
                creativeName: creativeName || undefined,
            }
        } finally {
            setIsUploading(false)
            setVideoUpload(null)
        }
    }

    return {
        files,
        adCopy,
        setAdCopy,
        productUrl,
        setProductUrl,
        creativeName,
        setCreativeName,
        isUploading,
        videoUpload,
        canSubmit,
        addFiles,
        removeFile,
        reset,
        uploadAndBuild,
    }
}

function isValidUrl(url: string): boolean {
    try {
        new URL(url)
        return true
    } catch {
        return false
    }
}

function uploadVideoFile(
    file: File,
    uploadUrl: string,
    onProgress: (progress: number) => void,
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                onProgress((e.loaded / e.total) * 100)
            }
        })

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve()
            } else {
                reject(new Error(`Upload failed: ${xhr.status}`))
            }
        })

        xhr.addEventListener('error', () => {
            reject(new Error('Upload failed'))
        })

        xhr.open('PUT', uploadUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.send(file)
    })
}
